import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  Badge,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  LocalHospital,
  People,
  Schedule,
  Assignment,
  Medication,
  Science,
  CameraAlt,
  Warning,
  CheckCircle,
  AccessTime,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`clinical-tabpanel-${index}`}
      aria-labelledby={`clinical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ClinicalOps: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { patients, addPatient, updatePatient, deletePatient } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    mrn: "",
    age: "",
    gender: "",
    department: "",
    status: "Inpatient",
    room: "",
    doctor: "",
    admissionDate: "",
  });

  const validationRules = {
    name: commonValidationRules.name,
    mrn: { required: true, minLength: 3 },
    age: { required: true, min: 0, max: 150 },
    gender: { required: true },
    department: { required: true },
    room: { required: true },
    doctor: { required: true },
    admissionDate: commonValidationRules.date,
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, patient?: any) => {
    setDialogType(type);
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        name: patient.name,
        mrn: patient.mrn,
        age: patient.age.toString(),
        gender: patient.gender,
        department: patient.department,
        status: patient.status,
        room: patient.room,
        doctor: patient.doctor,
        admissionDate: patient.admissionDate,
      });
    } else {
      setEditingPatient(null);
      setFormData({
        name: "",
        mrn: "",
        age: "",
        gender: "",
        department: "",
        status: "Inpatient",
        room: "",
        doctor: "",
        admissionDate: "",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingPatient(null);
    setFormData({
      name: "",
      mrn: "",
      age: "",
      gender: "",
      department: "",
      status: "Inpatient",
      room: "",
      doctor: "",
      admissionDate: "",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const patientData = {
        name: formData.name,
        mrn: formData.mrn,
        age: parseInt(formData.age),
        gender: formData.gender,
        department: formData.department,
        status: formData.status,
        room: formData.room,
        doctor: formData.doctor,
        admissionDate: formData.admissionDate,
      };

      if (editingPatient) {
        updatePatient(editingPatient.id, patientData);
        showSuccess("Patient updated successfully");
      } else {
        addPatient(patientData);
        showSuccess("Patient registered successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save patient");
    }
  };

  const handleDelete = (id: number) => {
    try {
      deletePatient(id);
      showSuccess("Patient deleted successfully");
    } catch (error) {
      showError("Failed to delete patient");
    }
  };

  // Mock data
  const patientsData = [
    {
      id: 1,
      name: "Ahmed Al-Rashid",
      mrn: "MRN-001234",
      age: 45,
      gender: "Male",
      admissionDate: "2024-01-15",
      department: "Cardiology",
      status: "Inpatient",
      room: "C-201",
      doctor: "Dr. Sarah Al-Rashid",
    },
    {
      id: 2,
      name: "Fatima Hassan",
      mrn: "MRN-001235",
      age: 32,
      gender: "Female",
      admissionDate: "2024-01-14",
      department: "Obstetrics",
      status: "Outpatient",
      room: "O-105",
      doctor: "Dr. Mohammed Al-Sayed",
    },
    {
      id: 3,
      name: "Omar Al-Zahra",
      mrn: "MRN-001236",
      age: 28,
      gender: "Male",
      admissionDate: "2024-01-16",
      department: "Emergency",
      status: "Emergency",
      room: "ER-03",
      doctor: "Dr. Ahmed Hassan",
    },
  ];

  const appointmentsData = [
    {
      id: 1,
      patient: "Ahmed Al-Rashid",
      doctor: "Dr. Sarah Al-Rashid",
      department: "Cardiology",
      date: "2024-01-20",
      time: "10:00 AM",
      type: "Follow-up",
      status: "Scheduled",
      duration: 30,
    },
    {
      id: 2,
      patient: "Fatima Hassan",
      doctor: "Dr. Mohammed Al-Sayed",
      department: "Obstetrics",
      date: "2024-01-21",
      time: "02:00 PM",
      type: "Consultation",
      status: "Confirmed",
      duration: 45,
    },
    {
      id: 3,
      patient: "Omar Al-Zahra",
      doctor: "Dr. Ahmed Hassan",
      department: "Emergency",
      date: "2024-01-22",
      time: "09:30 AM",
      type: "Emergency",
      status: "Pending",
      duration: 60,
    },
  ];

  const labResultsData = [
    {
      id: 1,
      patient: "Ahmed Al-Rashid",
      test: "Complete Blood Count",
      orderedBy: "Dr. Sarah Al-Rashid",
      orderedDate: "2024-01-15",
      completedDate: "2024-01-16",
      status: "Completed",
      results: "Normal",
      priority: "Routine",
    },
    {
      id: 2,
      patient: "Fatima Hassan",
      test: "Pregnancy Test",
      orderedBy: "Dr. Mohammed Al-Sayed",
      orderedDate: "2024-01-14",
      completedDate: "2024-01-14",
      status: "Completed",
      results: "Positive",
      priority: "Urgent",
    },
    {
      id: 3,
      patient: "Omar Al-Zahra",
      test: "X-Ray Chest",
      orderedBy: "Dr. Ahmed Hassan",
      orderedDate: "2024-01-16",
      completedDate: null,
      status: "In Progress",
      results: "Pending",
      priority: "High",
    },
  ];

  const imagingData = [
    {
      id: 1,
      patient: "Ahmed Al-Rashid",
      study: "CT Angiography",
      orderedBy: "Dr. Sarah Al-Rashid",
      orderedDate: "2024-01-15",
      scheduledDate: "2024-01-17",
      status: "Scheduled",
      modality: "CT",
      priority: "High",
    },
    {
      id: 2,
      patient: "Fatima Hassan",
      study: "Ultrasound Abdomen",
      orderedBy: "Dr. Mohammed Al-Sayed",
      orderedDate: "2024-01-14",
      scheduledDate: "2024-01-18",
      status: "Completed",
      modality: "Ultrasound",
      priority: "Routine",
    },
    {
      id: 3,
      patient: "Omar Al-Zahra",
      study: "MRI Brain",
      orderedBy: "Dr. Ahmed Hassan",
      orderedDate: "2024-01-16",
      scheduledDate: "2024-01-19",
      status: "In Progress",
      modality: "MRI",
      priority: "Urgent",
    },
  ];

  const pharmacyData = [
    {
      id: 1,
      patient: "Ahmed Al-Rashid",
      medication: "Metformin 500mg",
      prescribedBy: "Dr. Sarah Al-Rashid",
      prescribedDate: "2024-01-15",
      dispensedDate: "2024-01-15",
      quantity: 30,
      status: "Dispensed",
      instructions: "Take twice daily with meals",
    },
    {
      id: 2,
      patient: "Fatima Hassan",
      medication: "Prenatal Vitamins",
      prescribedBy: "Dr. Mohammed Al-Sayed",
      prescribedDate: "2024-01-14",
      dispensedDate: "2024-01-14",
      quantity: 60,
      status: "Dispensed",
      instructions: "Take once daily",
    },
    {
      id: 3,
      patient: "Omar Al-Zahra",
      medication: "Ibuprofen 400mg",
      prescribedBy: "Dr. Ahmed Hassan",
      prescribedDate: "2024-01-16",
      dispensedDate: null,
      quantity: 20,
      status: "Pending",
      instructions: "Take as needed for pain",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
      case "Dispensed":
      case "Confirmed":
        return "success";
      case "In Progress":
      case "Scheduled":
      case "Pending":
        return "warning";
      case "Emergency":
      case "Urgent":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Urgent":
      case "High":
        return "error";
      case "Routine":
        return "success";
      default:
        return "default";
    }
  };

  const upcomingAppointments = appointmentsData.filter(
    (apt) => apt.status === "Scheduled" || apt.status === "Confirmed"
  );
  const pendingLabResults = labResultsData.filter(
    (lab) => lab.status === "In Progress"
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("clinical.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Integrated clinical operations and patient care management
      </Typography>

      {/* Alerts */}
      {pendingLabResults.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pending Lab Results
          </Typography>
          {pendingLabResults.map((lab) => (
            <Typography key={lab.id} variant="body2">
              • {lab.patient} - {lab.test} (Ordered: {lab.orderedDate})
            </Typography>
          ))}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <People color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">1,234</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Patients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Schedule color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">45</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Appointments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Science color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">12</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Lab Results
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CameraAlt color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">8</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled Imaging
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Patient Management" />
            <Tab label="Appointments" />
            <Tab label="Lab Results" />
            <Tab label="Imaging" />
            <Tab label="Pharmacy" />
            <Tab label="Clinical Reports" />
          </Tabs>
        </Box>

        {/* Patient Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Patient Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("patient")}
            >
              Register Patient
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>MRN</TableCell>
                  <TableCell>Age/Gender</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Admission Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {patient.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </Avatar>
                        {patient.name}
                      </Box>
                    </TableCell>
                    <TableCell>{patient.mrn}</TableCell>
                    <TableCell>
                      {patient.age}/{patient.gender}
                    </TableCell>
                    <TableCell>{patient.department}</TableCell>
                    <TableCell>
                      <Chip
                        label={patient.status}
                        color={getStatusColor(patient.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{patient.room}</TableCell>
                    <TableCell>{patient.doctor}</TableCell>
                    <TableCell>{patient.admissionDate}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("patient", patient)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(patient.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Appointments Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Appointment Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("appointment")}
            >
              Schedule Appointment
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointmentsData.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>{appointment.patient}</TableCell>
                    <TableCell>{appointment.doctor}</TableCell>
                    <TableCell>{appointment.department}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {appointment.date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {appointment.time}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{appointment.type}</TableCell>
                    <TableCell>{appointment.duration} min</TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.status}
                        color={getStatusColor(appointment.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Lab Results Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Laboratory Results</Typography>
            <Button
              variant="contained"
              startIcon={<Science />}
              onClick={() => handleOpenDialog("lab")}
            >
              Order Lab Test
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Test</TableCell>
                  <TableCell>Ordered By</TableCell>
                  <TableCell>Ordered Date</TableCell>
                  <TableCell>Completed Date</TableCell>
                  <TableCell>Results</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labResultsData.map((lab) => (
                  <TableRow key={lab.id}>
                    <TableCell>{lab.patient}</TableCell>
                    <TableCell>{lab.test}</TableCell>
                    <TableCell>{lab.orderedBy}</TableCell>
                    <TableCell>{lab.orderedDate}</TableCell>
                    <TableCell>{lab.completedDate || "-"}</TableCell>
                    <TableCell>{lab.results}</TableCell>
                    <TableCell>
                      <Chip
                        label={lab.priority}
                        color={getPriorityColor(lab.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lab.status}
                        color={getStatusColor(lab.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Imaging Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Imaging Studies</Typography>
            <Button
              variant="contained"
              startIcon={<CameraAlt />}
              onClick={() => handleOpenDialog("imaging")}
            >
              Order Imaging
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Study</TableCell>
                  <TableCell>Modality</TableCell>
                  <TableCell>Ordered By</TableCell>
                  <TableCell>Ordered Date</TableCell>
                  <TableCell>Scheduled Date</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imagingData.map((imaging) => (
                  <TableRow key={imaging.id}>
                    <TableCell>{imaging.patient}</TableCell>
                    <TableCell>{imaging.study}</TableCell>
                    <TableCell>{imaging.modality}</TableCell>
                    <TableCell>{imaging.orderedBy}</TableCell>
                    <TableCell>{imaging.orderedDate}</TableCell>
                    <TableCell>{imaging.scheduledDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={imaging.priority}
                        color={getPriorityColor(imaging.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={imaging.status}
                        color={getStatusColor(imaging.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Pharmacy Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Pharmacy Management</Typography>
            <Button
              variant="contained"
              startIcon={<Medication />}
              onClick={() => handleOpenDialog("pharmacy")}
            >
              Prescribe Medication
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Medication</TableCell>
                  <TableCell>Prescribed By</TableCell>
                  <TableCell>Prescribed Date</TableCell>
                  <TableCell>Dispensed Date</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Instructions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pharmacyData.map((med) => (
                  <TableRow key={med.id}>
                    <TableCell>{med.patient}</TableCell>
                    <TableCell>{med.medication}</TableCell>
                    <TableCell>{med.prescribedBy}</TableCell>
                    <TableCell>{med.prescribedDate}</TableCell>
                    <TableCell>{med.dispensedDate || "-"}</TableCell>
                    <TableCell>{med.quantity}</TableCell>
                    <TableCell>{med.instructions}</TableCell>
                    <TableCell>
                      <Chip
                        label={med.status}
                        color={getStatusColor(med.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Clinical Reports Tab */}
        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" gutterBottom>
            Clinical Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Patient Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Comprehensive patient care summary and history
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Department Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Clinical outcomes and performance metrics by department
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quality Metrics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Quality indicators and patient safety metrics
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "patient" && (editingPatient ? "Edit Patient" : "Register New Patient")}
          {dialogType === "appointment" && "Schedule Appointment"}
          {dialogType === "lab" && "Order Lab Test"}
          {dialogType === "imaging" && "Order Imaging Study"}
          {dialogType === "pharmacy" && "Prescribe Medication"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "patient" ? (
              <>
                <TextField
                  fullWidth
                  label="Patient Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="MRN/ID"
                  variant="outlined"
                  value={formData.mrn}
                  onChange={(e) => handleInputChange("mrn", e.target.value)}
                  error={!!errors.mrn}
                  helperText={errors.mrn}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  variant="outlined"
                  value={formData.age}
                  onChange={(e) => handleInputChange("age", e.target.value)}
                  error={!!errors.age}
                  helperText={errors.age}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select 
                    label="Gender"
                    value={formData.gender}
                    onChange={(e) => handleInputChange("gender", e.target.value)}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Department</InputLabel>
                  <Select 
                    label="Department"
                    value={formData.department}
                    onChange={(e) => handleInputChange("department", e.target.value)}
                  >
                    <MenuItem value="Cardiology">Cardiology</MenuItem>
                    <MenuItem value="Orthopedics">Orthopedics</MenuItem>
                    <MenuItem value="Pediatrics">Pediatrics</MenuItem>
                    <MenuItem value="Emergency">Emergency</MenuItem>
                    <MenuItem value="Surgery">Surgery</MenuItem>
                    <MenuItem value="Obstetrics">Obstetrics</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select 
                    label="Status"
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  >
                    <MenuItem value="Inpatient">Inpatient</MenuItem>
                    <MenuItem value="Outpatient">Outpatient</MenuItem>
                    <MenuItem value="Emergency">Emergency</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Room"
                  variant="outlined"
                  value={formData.room}
                  onChange={(e) => handleInputChange("room", e.target.value)}
                  error={!!errors.room}
                  helperText={errors.room}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Doctor"
                  variant="outlined"
                  value={formData.doctor}
                  onChange={(e) => handleInputChange("doctor", e.target.value)}
                  error={!!errors.doctor}
                  helperText={errors.doctor}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Admission Date"
                  type="date"
                  variant="outlined"
                  value={formData.admissionDate}
                  onChange={(e) => handleInputChange("admissionDate", e.target.value)}
                  error={!!errors.admissionDate}
                  helperText={errors.admissionDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Patient Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="MRN/ID"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Department</InputLabel>
                  <Select label="Department">
                    <MenuItem value="cardiology">Cardiology</MenuItem>
                    <MenuItem value="orthopedics">Orthopedics</MenuItem>
                    <MenuItem value="pediatrics">Pediatrics</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                    <MenuItem value="surgery">Surgery</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={dialogType === "patient" ? handleSubmit : handleCloseDialog}>
            {dialogType === "patient" ? (editingPatient ? "Update" : "Register") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicalOps;
